import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faShield,
  faStar,
  faUserCheck,
  faUserXmark,
} from '@fortawesome/free-solid-svg-icons';

import {
  Card,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
} from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { Paginator } from '@/components/ui/Paginator/Paginator';
import { useModalStore, useAuthStore } from '@/stores';
import { useUsers, useUpdateUser } from '@/api/useUsers';
import type { UserListItem } from '@/api/useUsers';

import { CreateUserModal } from './CreateUserModal';
import { UserDetailModal } from './UserDetailModal';
import styles from './Users.module.css';

// ─── Confirm Action Modal ─────────────────────────────────────────────────────

interface ConfirmActionModalProps {
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  handleClose?: () => void;
}

function ConfirmActionModal({
  message,
  confirmLabel,
  onConfirm,
  handleClose,
}: ConfirmActionModalProps) {
  const { closeModal } = useModalStore();

  const dismiss = () => {
    if (handleClose) handleClose();
    else closeModal();
  };

  const handleConfirm = () => {
    onConfirm();
    dismiss();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', padding: '0.5rem 0' }}>
      <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--color-text)' }}>{message}</p>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <Button variant="secondary" size="sm" onClick={dismiss}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={handleConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 15;

const ROLE_FILTER_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'provider', label: 'Provider' },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getEffectiveRoleLabel(user: UserListItem): string {
  if (user.is_superuser) return 'Admin';
  if (user.is_staff) return 'Staff';
  if (user.role === 'provider') return 'Provider';
  return 'Unassigned';
}

function EffectiveRoleBadge({ user }: { user: UserListItem }) {
  const label = getEffectiveRoleLabel(user);
  const cls =
    label === 'Admin'
      ? styles.roleAdmin
      : label === 'Staff'
        ? styles.roleStaff
        : label === 'Provider'
          ? styles.roleProvider
          : styles.roleUnassigned;
  return <span className={`${styles.badge} ${cls}`}>{label}</span>;
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span className={`${styles.badge} ${isActive ? styles.statusActive : styles.statusInactive}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

export function Users() {
  const { openModal } = useModalStore();
  const { isAdmin } = useAuthStore();
  const currentUserIsAdmin = isAdmin();
  const updateUser = useUpdateUser();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);

  const apiFilters = useMemo(() => {
    const f: Record<string, string> = {};
    if (roleFilter) f.role = roleFilter;
    if (activeFilter) f.is_active = activeFilter;
    if (search.trim()) f.search = search.trim();
    return Object.keys(f).length > 0 ? f : undefined;
  }, [roleFilter, activeFilter, search]);

  const { data: users = [], isLoading, isError } = useUsers(apiFilters);

  // Staff cannot see superuser (admin) accounts
  const visibleUsers = useMemo(() => {
    if (currentUserIsAdmin) return users;
    return users.filter((u) => !u.is_superuser);
  }, [users, currentUserIsAdmin]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return visibleUsers;
    const s = search.toLowerCase();
    return visibleUsers.filter(
      (u) =>
        u.email.toLowerCase().includes(s) ||
        u.first_name.toLowerCase().includes(s) ||
        u.last_name.toLowerCase().includes(s) ||
        u.username.toLowerCase().includes(s),
    );
  }, [visibleUsers, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paginatedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  const handleRoleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRoleFilter(e.target.value);
    setPage(1);
  };

  const handleActiveFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setActiveFilter(e.target.value);
    setPage(1);
  };

  const handleRowClick = (user: UserListItem) => {
    openModal(<UserDetailModal userId={user.id} />);
  };

  const handleOpenCreateModal = () => {
    openModal(<CreateUserModal />);
  };

  return (
    <div className={styles.container}>
      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h2 className={styles.pageTitle}>Users</h2>
          <Button variant="primary" onClick={handleOpenCreateModal}>
            <FontAwesomeIcon icon={faPlus} /> Create User
          </Button>
        </div>

        <div className={styles.filters}>
          <Input
            name="search"
            label="Search"
            placeholder="Search by email, name or username..."
            value={search}
            setValue={handleSearchChange}
            variant="bordered"
            isClearable
          />
          <Select
            label="Role"
            value={roleFilter}
            onChange={handleRoleFilterChange}
            options={ROLE_FILTER_OPTIONS}
          />
          <Select
            label="Status"
            value={activeFilter}
            onChange={handleActiveFilterChange}
            options={ACTIVE_FILTER_OPTIONS}
          />
        </div>

        <p className={styles.clickHint}>
          Click any row to view full details. Use the action buttons for quick access.
        </p>

        <Table aria-label="Users list" radius={8}>
          <TableHeader>
            <TableColumn>Email</TableColumn>
            <TableColumn>Name</TableColumn>
            <TableColumn>Effective Role</TableColumn>
            <TableColumn>Status</TableColumn>
            <TableColumn>Providers</TableColumn>
            <TableColumn>Last Login</TableColumn>
            <TableColumn>Actions</TableColumn>
          </TableHeader>
          <TableBody isLoading={isLoading}>
            {isError ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className={styles.emptyState}>Failed to load users. Please try again.</div>
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className={styles.emptyState}>No users found.</div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <TableRow
                  key={user.id}
                  className={styles.clickableRow}
                  onClick={() => handleRowClick(user)}
                >
                  <TableCell>
                    <span className={styles.emailCell}>{user.email}</span>
                  </TableCell>
                  <TableCell>
                    {user.first_name || user.last_name ? (
                      `${user.first_name} ${user.last_name}`.trim()
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <EffectiveRoleBadge user={user} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge isActive={user.is_active} />
                  </TableCell>
                  <TableCell>
                    <span className={styles.providerCount}>{user.provider_count ?? 0}</span>
                  </TableCell>
                  <TableCell>
                    <span className={styles.muted}>{formatDate(user.last_login)}</span>
                  </TableCell>
                  <TableCell>
                    <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
                      {/* Staff toggle */}
                      <button
                        className={`${styles.actionIcon} ${user.is_staff ? styles.actionRevoke : styles.actionGrant}`}
                        title={user.is_staff ? 'Remove staff access' : 'Grant staff access'}
                        disabled={updateUser.isPending}
                        onClick={() =>
                          openModal(
                            <ConfirmActionModal
                              message={
                                user.is_staff
                                  ? `Remove staff access for ${user.email}?`
                                  : `Grant staff access for ${user.email}?`
                              }
                              confirmLabel={user.is_staff ? 'Remove' : 'Grant'}
                              onConfirm={() =>
                                updateUser.mutate({ id: user.id, is_staff: !user.is_staff })
                              }
                            />,
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faStar} />
                      </button>

                      {/* Deactivate / Activate */}
                      <button
                        className={`${styles.actionIcon} ${user.is_active ? styles.actionRevoke : styles.actionGrant}`}
                        title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        disabled={updateUser.isPending}
                        onClick={() =>
                          openModal(
                            <ConfirmActionModal
                              message={
                                user.is_active
                                  ? `Deactivate ${user.email}?`
                                  : `Activate ${user.email}?`
                              }
                              confirmLabel={user.is_active ? 'Deactivate' : 'Activate'}
                              onConfirm={() =>
                                updateUser.mutate({ id: user.id, is_active: !user.is_active })
                              }
                            />,
                          )
                        }
                      >
                        <FontAwesomeIcon icon={user.is_active ? faUserXmark : faUserCheck} />
                      </button>

                      {/* Admin toggle — admin only */}
                      {currentUserIsAdmin && (
                        <button
                          className={`${styles.actionIcon} ${user.is_superuser ? styles.actionRevoke : styles.actionGrant}`}
                          title={user.is_superuser ? 'Remove admin access' : 'Grant admin access'}
                          disabled={updateUser.isPending}
                          onClick={() =>
                            openModal(
                              <ConfirmActionModal
                                message={
                                  user.is_superuser
                                    ? `Remove admin access for ${user.email}?`
                                    : `Grant admin access for ${user.email}?`
                                }
                                confirmLabel={user.is_superuser ? 'Remove' : 'Grant'}
                                onConfirm={() =>
                                  updateUser.mutate({
                                    id: user.id,
                                    is_superuser: !user.is_superuser,
                                  })
                                }
                              />,
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faShield} />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Paginator page={page} numPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}
